import React from 'react';
import styled from 'styled-components';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Button,
	Checkbox,
	FormControlLabel,
	IconButton,
	InputAdornment,
	TextField,
} from '@mui/material';
import {
	Controller,
	useForm,
} from 'react-hook-form';
import {
	Visibility as VisibilityIcon,
	VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import useAxios from 'axios-hooks';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { changeModals, NModals } from '../modals';
import { ModalLayout } from '../layout';
import yupValidationResolver from '../../../utils/yupValidationResolver';
import schema from './schema';
import {
	API_ROUTES,
	ROUTES,
} from '../../../routes';
import { Auth } from '../../../utils/auth';
import { getUniqueId } from '../../../scripts/unique-id-manager';
import { InfoHover } from '../../info-hover';
import IconDiscord from '../../icons/icon-discord';
import {
	DISCORD_AUTH_REDIRECT,
	DISCORD_AUTH_SCOPE,
	DISCORD_CLIENT_ID,
} from '../../../const';
import {
	useDispatch,
	useSelector,
} from '../../../store/store';
import { fetchUser } from '../../../store/slices/user';

const getDefaultForm = (
	props?: Partial<NModalUserSettings.IForm>,
): NModalUserSettings.IForm => {
	return {
		email: '',
		username: '',
		unlisted: false,
		password: '',
		...props,
	};
};

const name = 'ModalUserSettings';
const Modal = (
	props: NModalUserSettings.IProps,
) => {
	const { user } = useSelector((st) => st.user);
	const dispatch = useDispatch();

	const navigate = useNavigate();

	const [openAccordion, setOpenAccordion] =
		React.useState<string>('generic');

	const openAccordionHandler = (id: string) => {
		setOpenAccordion(id);
	};

	const [, updateUserSettings] = useAxios(
		{
			method: 'POST',
			url: API_ROUTES.updateUserSettings,
		},
		{ manual: true, autoCancel: true },
	);

	const [
		showCurrentPassword,
		setShowCurrentPassword,
	] = React.useState<boolean>(false);

	const closeModal = () => {
		window.dispatchEvent(
			changeModals({ [name]: null }),
		);
	};

	const {
		handleSubmit,
		formState: { errors: formErrors },
		register,
		reset: resetForm,
		control,
	} = useForm<NModalUserSettings.IForm>({
		resolver: yupValidationResolver(schema()),
		defaultValues: getDefaultForm(),
	});

	const onSubmit = handleSubmit(
		async (values) => {
			const resp = await updateUserSettings({
				headers: {
					Authorization: `Bearer ${Auth.getToken()}`,
					uniqueId: getUniqueId(),
				},
				data: {
					...values,
				},
			});

			if (resp?.data?.data?.success) {
				toast('Settings updated', {
					type: 'success',
				});
				dispatch(fetchUser());
				closeModal();
			}
		},
	);

	const doConnect = (type: 'discord') => {
		switch (type) {
			case 'discord': {
				window.location.href = `https://discord.com/oauth2/authorize?client_id=${DISCORD_CLIENT_ID}&response_type=code&redirect_uri=${DISCORD_AUTH_REDIRECT}&scope=${DISCORD_AUTH_SCOPE}&state=${getUniqueId()}`;
				break;
			}
			default:
				break;
		}
	};

	const doDisconnect = async (
		type: 'discord',
	) => {
		switch (type) {
			case 'discord': {
				try {
					await axios.post(
						API_ROUTES.authDiscordDisconnect,
						undefined,
						{
							headers: {
								Authorization: `Bearer ${Auth.getToken()}`,
								uniqueId: getUniqueId(),
							},
						},
					);
				} catch (err) {
					console.error('Something went wrong');
				}
				dispatch(fetchUser());
				break;
			}
			default:
				break;
		}
	};

	const goResetPassword = () => {
		navigate(ROUTES.resetPassword);
		closeModal();
	};

	React.useEffect(() => {
		resetForm(
			getDefaultForm({
				email: user?.email || '',
				username: user?.username || '',
				unlisted: user?.unlisted_profile || false,
			}),
		);
	}, [user]);

	return (
		<ModalLayout
			{...props}
			showHeader
			hideCloseButton
			title="Settings"
			name={name}
			width="500px"
		>
			<ModalUserSettingsStyle>
				<form>
					<div className="form-body">
						<Accordion
							expanded={
								openAccordion === 'generic'
							}
							onChange={() =>
								openAccordionHandler('generic')
							}
						>
							<AccordionSummary>
								<h3>Generic</h3>
							</AccordionSummary>
							<AccordionDetails>
								<Controller
									name="email"
									control={control}
									render={({ field }) => (
										<TextField
											{...field}
											helperText={
												formErrors.email?.message
											}
											error={
												!!formErrors.email
													?.message
											}
											size="small"
											label="Email"
										/>
									)}
								/>
								<Controller
									name="username"
									control={control}
									render={({ field }) => (
										<TextField
											{...field}
											helperText={
												formErrors.username
													?.message
											}
											error={
												!!formErrors.username
													?.message
											}
											size="small"
											label="Username"
										/>
									)}
								/>
							</AccordionDetails>
						</Accordion>
						<Accordion
							expanded={
								openAccordion === 'privacy'
							}
							onChange={() =>
								openAccordionHandler('privacy')
							}
						>
							<AccordionSummary>
								<h3>Privacy</h3>
							</AccordionSummary>
							<AccordionDetails>
								<Controller
									name="unlisted"
									control={control}
									render={({ field }) => (
										<FormControlLabel
											label={
												<div className="checkbox-label">
													<span>
														Unlisted profile
													</span>
													<InfoHover text="Whether your profile should be unlisted from public lists. This does not hide your profile." />
												</div>
											}
											control={
												<Checkbox
													{...register(
														'unlisted',
													)}
													checked={field.value}
												/>
											}
										/>
									)}
								/>
							</AccordionDetails>
						</Accordion>
						<Accordion
							expanded={
								openAccordion === 'connections'
							}
							onChange={() =>
								openAccordionHandler(
									'connections',
								)
							}
						>
							<AccordionSummary>
								<h3>Connections</h3>
							</AccordionSummary>
							<AccordionDetails>
								<div className="connection-row">
									<h4>
										<IconDiscord /> Discord
										{user?.discord ? (
											<span className="user-discord-username">
												{user?.discord?.username}
											</span>
										) : null}
									</h4>
									<div className="connection-row-buttons">
										<Button
											size="small"
											onClick={() =>
												doConnect('discord')
											}
										>
											{user?.discord
												? 'Update'
												: 'Connect'}
										</Button>
										<Button
											size="small"
											disabled={!user?.discord}
											onClick={() =>
												doDisconnect('discord')
											}
										>
											Disconnect
										</Button>
									</div>
								</div>
							</AccordionDetails>
						</Accordion>
						<div className="form-security">
							<h3>Security</h3>
							<div className="security-buttons">
								<Button onClick={goResetPassword}>
									Reset password
								</Button>
							</div>
						</div>
						<div className="non-form">
							<h3>Confirm</h3>
							<TextField
								helperText={
									formErrors.password?.message
								}
								error={
									!!formErrors.password?.message
								}
								size="small"
								label="Current password"
								type={
									showCurrentPassword
										? 'text'
										: 'password'
								}
								InputProps={{
									...register('password'),
									endAdornment: (
										<InputAdornment position="end">
											<IconButton
												size="small"
												aria-label="toggle password visibility"
												onClick={() =>
													setShowCurrentPassword(
														(v) => !v,
													)
												}
												onMouseDown={() =>
													setShowCurrentPassword(
														(v) => !v,
													)
												}
											>
												{showCurrentPassword ? (
													<VisibilityIcon fontSize="small" />
												) : (
													<VisibilityOffIcon fontSize="small" />
												)}
											</IconButton>
										</InputAdornment>
									),
								}}
							/>
						</div>
					</div>
					<div className="buttons">
						<Button onClick={closeModal}>
							Close
						</Button>
						<Button onClick={onSubmit}>
							Save
						</Button>
					</div>
				</form>
			</ModalUserSettingsStyle>
		</ModalLayout>
	);
};

export { name, Modal };

export namespace NModalUserSettings {
	// eslint-disable-next-line @typescript-eslint/no-empty-interface
	export interface IProps
		extends NModals.IDefaultProps {
		//
	}

	export interface IForm {
		email: string;
		username: string;
		unlisted: boolean;
		password: string;
	}
}

const ModalUserSettingsStyle = styled.div`
	.form-body {
		display: flex;
		flex-direction: column;
		row-gap: 15px;
		> * {
			display: flex;
			flex-direction: column;
			row-gap: 10px;
		}
	}
	.MuiAccordion-root {
		&.Mui-expanded {
			margin: 0;
		}
		.MuiAccordionSummary-root {
			//
		}
		.MuiAccordionDetails-root {
			display: flex;
			flex-direction: column;
			row-gap: 10px;
		}
	}
	.user-discord-username {
		margin-left: 5px;
		font-size: 12px;
		color: ${({ theme }) =>
			theme?.palette?.text?.secondary};
	}
	.connection-row {
		> h4 {
			margin-bottom: 5px;
		}
		.connection-row-buttons {
			display: flex;
			column-gap: 5px;
		}
	}
	.checkbox-label {
		display: flex;
		align-items: center;
		column-gap: 5px;
	}
	.buttons {
		margin-top: 10px;
		display: flex;
		justify-content: flex-end;
	}
`;
